import { useEffect, useState } from "react";
import api, { formatTL, formatApiError } from "@/lib/api";
import { Plus, Edit, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#0077B6]";
const empty = { name: "", slug: "", category_id: "", description: "", price: 0, old_price: 0, stock: 0, images: [""], is_active: true, is_featured: false, weight_kg: 1 };

export default function AdminProducts() {
  const [list, setList] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/products?limit=200"), api.get("/categories")]);
    setList(p.data); setCats(c.data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...empty, ...p, images: p.images?.length ? p.images : [""] }); setEditId(p.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: Number(form.price), old_price: form.old_price ? Number(form.old_price) : null, stock: Number(form.stock), weight_kg: Number(form.weight_kg), images: form.images.filter(Boolean) };
    try {
      if (editId) await api.put(`/admin/products/${editId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Kaydedildi");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Silindi"); load();
  };

  const setImg = (i, v) => { const im = [...form.images]; im[i] = v; setForm({ ...form, images: im }); };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Ürünler</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openCreate} className="btn-primary" data-testid="add-product"><Plus size={16}/> Yeni Ürün</button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editId ? "Ürünü Düzenle" : "Yeni Ürün"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input required placeholder="Ürün adı" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || e.target.value.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") })} />
                <input required placeholder="Slug" className={inputCls} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
                <select required className={inputCls} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                  <option value="">Kategori seçin</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <input type="number" step="0.01" required placeholder="Ağırlık (kg)" className={inputCls} value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} />
                <input type="number" step="0.01" required placeholder="Fiyat" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                <input type="number" step="0.01" placeholder="Eski fiyat" className={inputCls} value={form.old_price || ""} onChange={(e) => setForm({ ...form, old_price: e.target.value })} />
                <input type="number" required placeholder="Stok" className={inputCls + " sm:col-span-2"} value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
              </div>
              <textarea placeholder="Açıklama" rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <div>
                <div className="text-xs font-bold mb-2">Görsel URL'leri</div>
                {form.images.map((img, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input placeholder="https://..." className={inputCls} value={img} onChange={(e) => setImg(i, e.target.value)} />
                    <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })} className="text-rose-600"><X size={16}/></button>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, images: [...form.images, ""] })} className="text-xs text-[#0077B6] font-bold">+ Görsel ekle</button>
              </div>
              <div className="flex gap-5 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Aktif</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Öne çıkan</label>
              </div>
              <button data-testid="save-product" className="btn-primary w-full">Kaydet</button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left">
            <th className="p-3">Ürün</th><th>Kategori</th><th>Fiyat</th><th>Stok</th><th>Durum</th><th></th>
          </tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t">
                <td className="p-3 flex items-center gap-3">
                  <img src={p.images?.[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />
                  <div><div className="font-semibold">{p.name}</div><div className="text-xs text-slate-500">{p.slug}</div></div>
                </td>
                <td>{cats.find((c) => c.id === p.category_id)?.name}</td>
                <td className="font-semibold">{formatTL(p.price)}</td>
                <td>{p.stock}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full ${p.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100"}`}>{p.is_active ? "Aktif" : "Pasif"}</span></td>
                <td className="text-right pr-4">
                  <button onClick={() => openEdit(p)} className="p-2 text-[#0077B6]" data-testid={`edit-${p.slug}`}><Edit size={15}/></button>
                  <button onClick={() => del(p.id)} className="p-2 text-rose-600" data-testid={`del-${p.slug}`}><Trash2 size={15}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
